import React, { useState, useRef, useEffect } from 'react';
import {X, Minus, Square, User, Mail, Github, Linkedin, Camera, Code, Heart, Star, Instagram} from 'lucide-react';
import {FaDiscord} from "react-icons/fa";

const CharacterDonutPortfolio = () => {
    const [openWindows, setOpenWindows] = useState([]);
    const [windowPositions, setWindowPositions] = useState({});
    const [draggedWindow, setDraggedWindow] = useState(null);
    const [dragOffset, setDragOffset] = useState({x: 0, y: 0});
    const [time, setTime] = useState(new Date());
    const [hoveredItem, setHoveredItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 500);
    const [githubStats, setGithubStats] = useState({
        repos: 0,
        followers: 0,
        following: 0,
        stars: 0,
        topLanguages: [],
        topRepo: null,
        lastActivity: '',
        joinDate: '',
        totalContributions: 0,
        totalCommits: 0,
        recentCommits: [],
        codingStreak: 0
    });
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectTab, setProjectTab] = useState('overview');

    const characterDonuts = [
        {
            id: 'about me',
            name: 'About Me',
            x: '50%',
            y: '37%',
            image: '/heart.png'
        },
        {
            id: 'my projects',
            name: 'My Projects',
            x: '35%',
            y: '44%',
            image: '/star.png'
        },
        {
            id: 'github',
            name: 'GitHub',
            x: '38%',
            y: '56%',
            image: '/cat.png'
        },
        {
            id: 'contact',
            name: 'Contact',
            x: '50%',
            y: '64%',
            image: '/naruto.png'
        },
        {
            id: 'blog',
            name: 'Blog',
            x: '63%',
            y: '58%',
            image: '/ribbon.png'
        },
        {
            id: 'resume',
            name: 'Resume',
            x: '62%',
            y: '46%',
            image: '/wing.png'
        }
    ];

    const detailedProjects = [
        {
            id: 'concert',
            name: '콘서트 예약 서비스',
            emoji: '🎶',
            color: 'from-indigo-200 to-purple-200',
            period: '2024.10 - 2024.11',
            teamSize: '개인 프로젝트',
            techDetail: {
                backend: ['Spring Boot 3.0', 'JPA', 'QueryDSL', 'Gradle'],
                database: ['MySQL 8.0', 'Redis'],
                infra: ['Docker', 'AWS EC2', 'GitHub Actions'],
                testing: ['JUnit5', 'JMeter', 'Testcontainers']
            },
            achievements: [
                '동시 예약 처리를 위한 비관적 락과 조건부 UPDATE 구현',
                '대기열 시스템 설계 및 구현 (MySQL 기반, 100명 동시 처리)',
                'JMeter를 활용한 부하 테스트 수행 (TPS 500+)',
                '클린 아키텍처 적용으로 도메인 계층 순수성 확보',
                '멀티스레드 동시성 테스트 프레임워크 구축'
            ],
            challenges: '초기 헥사고날 아키텍처에서 클린 아키텍처로 전환하면서 의존성 방향 정리가 가장 어려웠습니다. 특히 동시성 제어에서 낙관적 락의 재시도 지옥을 경험한 후, 비관적 락과 조건부 UPDATE를 적절히 조합하여 해결했습니다.',
            keyFeatures: {
                '대기열 관리': '토큰 기반 대기열 시스템 (활성 사용자 100명 제한)',
                '좌석 예약': '5분 임시 배정 후 자동 해제 (스케줄러 구현)',
                '결제 시스템': '멱등성 보장 및 동시 차감 방지',
                '동시성 제어': '비관적 락과 유니크 제약조건 조합'
            },
            performanceMetrics: {
                '동시 사용자': '1000명 동시 접속 처리',
                'TPS': '500+ (좌석 예약 기준)',
                '응답 시간': '평균 200ms 이내',
                '처리 정확도': '100% (중복 예약 0건)'
            },
            repo: 'hhplus-server'
        },
        {
            id: 'hanjeok',
            name: '한적서울',
            emoji: '🏙️',
            color: 'from-green-200 to-blue-200',
            period: '2025.03 - 2025.05',
            teamSize: '백엔드 2, 데이터 1, 프론트 1',
            techDetail: {
                backend: ['Spring Boot', 'JWT 인증', 'REST API'],
                database: ['MySQL (RDS)', 'Pandas', 'Python'],
                infra: ['AWS EC2', 'S3', 'Docker', 'GitHub Actions', 'Render'],
                api: ['서울시 실시간 인구 API', '카카오맵 API'],
                frontend: ['React Native (Expo Router)'],
                docs: ['Swagger']
            },
            achievements: [
                '서울시 실시간 인구 데이터를 활용한 조용한 장소 추천 알고리즘 구현',
                'Spring Boot 기반 RESTful API 서버 설계 및 20개 이상 엔드포인트 구축',
                'JWT 기반 인증 및 예외 처리 로직 구현',
                'Pandas 기반 CSV 전처리 및 자동 DB 삽입 스크립트 개발',
                'AWS S3 이미지 업로드 및 URL 관리 기능 구현',
                'EC2 + Docker 기반 CI/CD 구축 후 Render로 HTTPS 대응 배포 전환',
                'Expo 빌드 오류 해결 및 Play Store 릴리스 주도',
                'Swagger를 통한 API 문서화 및 프론트 협업 효율화'
            ],
            challenges:
                '2만 건 이상의 장소 데이터를 전처리하며 좌표계 변환 정밀도 손실 문제와 실시간 인구 데이터 매핑의 효율성을 동시에 해결해야 했습니다. ' +
                '또한 EC2 배포 환경에서 HTTPS 인증과 포트 접근 제한 문제를 마주하며, Render로 전환해 보안성과 접근성을 확보했습니다. ' +
                'Expo 앱 빌드 오류를 직접 해결하며 서비스 완성도까지 책임졌습니다.',
            keyFeatures: {
                '위치 기반 추천': '사용자 좌표와 서울시 116개 지역의 혼잡도를 비교해 가장 한적한 장소 추천',
                '커뮤니티 기능': '제보 등록 → 관리자 승인 → 후기 작성 → 이미지 업로드의 연결 구조 구현',
                '데이터 처리': 'Python + Pandas 기반 대용량 CSV 자동 삽입 및 좌표계 변환',
                '파일 업로드': 'S3 연동, URL 저장, 접근 권한 및 기본 이미지 처리 포함',
                'CI/CD 및 배포': 'EC2 + Docker 자동 배포 구성 후 Render HTTPS 대응 환경으로 전환',
                '문서화 및 협업': 'Swagger 기반 API 문서 제공 및 프론트와의 실시간 피드백 반영'
            },
            performanceMetrics: {
                '장소 데이터 처리량': '약 26,000건 자동 삽입 처리',
                '배포': 'Play Store 배포'
            },
            repo: 'hanjeokSeoul_back'
        },
        {
            id: 'plannie',
            name: 'Plannie',
            emoji: '📅',
            color: 'from-blue-200 to-purple-200',
            period: '2024.05 - 2024.10',
            teamSize: '풀스택 2, 데이터 1',
            techDetail: {
                frontend: ['React Native', 'Expo', 'React Navigation'],
                backend: ['Node.js', 'Express.js'],
                database: ['MongoDB', 'Amazon RDS (MySQL)'],
                infra: ['AWS EC2', 'Docker'],
                ai: ['Chat-based AI Study Planner']
            },
            achievements: [
                'AI 채팅 기반 공부 계획 자동 생성 기능 설계 및 구현',
                'React Native 기반 모바일 앱 UI/UX 개발 및 최적화',
                'MongoDB와 Amazon RDS를 연동한 데이터베이스 설계 및 구축',
                'RESTful API 설계 및 인증/예외 처리 로직 강화',
                'Docker를 활용한 AWS EC2 배포 환경 구성 시도',
                '프론트엔드부터 백엔드까지 서비스 전체 개발 프로세스 참여'
            ],
            challenges:
                'AI 채팅 기능과 일정 관리 기능의 데이터 흐름을 통합하는 과정에서 실시간성과 일관성을 유지하는 것이 어려웠습니다. ' +
                'API 통신 구조를 재설계하고, MongoDB와 RDS 간 데이터 동기화 전략을 적용하여 문제를 해결했습니다.',
            keyFeatures: {
                'AI 공부 계획 생성': '사용자 채팅 입력을 분석해 개인 맞춤형 공부 계획을 자동 생성',
                '일정 관리': '캘린더 뷰 기반 일정 관리 및 학습 리마인더 제공',
                '실시간 채팅': 'AI와 사용자 간의 대화형 플래닝 시스템',
                '데이터 연동': 'MongoDB와 RDS 간 데이터 통합 관리',
                '배포 환경 구성': 'Docker 기반 컨테이너 환경을 통한 서비스 시연 및 배포 검증'
            },
            repo: 'Plannie'
        }
    ];

    const velogSeries = [
        {
            id: 1,
            title: "항해 플러스",
            description: "항해 플러스 TIL & 회고",
            thumbnail: "🚢",
            link: "https://velog.io/@xaexix/series/항해-플러스"
        },
        {
            id: 2,
            title: "JVM 라이브러리 코드 리딩",
            description: "JVM 라이브러리 코드 리딩 공부",
            thumbnail: "🖥️",
            link: "https://velog.io/@xaexix/series/JVM-라이브러리-코드-리딩"
        }
    ];

    const IconDisplay = ({ item, size = 'normal' }) => {
        const [imageError, setImageError] = useState(false);
        const sizeClasses = {
            small: 'w-8 h-8',
            normal: 'w-12 h-12',
            large: 'w-16 h-16'
        };

        if (!item.image || imageError) {
            return <div className={`${sizeClasses[size]} flex items-center justify-center text-4xl`}>🍩</div>;
        }

        return (
            <img
                src={item.image}
                alt={item.name}
                className={`${sizeClasses[size]} object-contain filter drop-shadow-lg`}
                onError={() => setImageError(true)}
            />
        );
    };

    // 시간 업데이트
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // GitHub 통계 가져오기
    useEffect(() => {
        fetchGithubStats();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsSmallScreen(window.innerWidth < 500);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ESC 키로 가장 최근 윈도우 닫기
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (openWindows.length > 0) {
                    const lastWindow = openWindows[openWindows.length - 1];
                    closeWindow(lastWindow.id);
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [openWindows]);

    // 마우스 이동 시 윈도우 드래그만 처리
    const handleMouseMove = (e) => {
        if (draggedWindow) {
            setWindowPositions(prev => ({
                ...prev,
                [draggedWindow]: {
                    x: e.clientX - dragOffset.x,
                    y: e.clientY - dragOffset.y
                }
            }));
        }
    };

    // 윈도우 드래그 핸들러
    const handleMouseDown = (e, windowId) => {
        const windowElement = e.currentTarget.parentElement;
        const rect = windowElement.getBoundingClientRect();
        setDraggedWindow(windowId);
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    const handleMouseUp = () => {
        setDraggedWindow(null);
    };

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggedWindow, dragOffset]);

    // 윈도우 열기
    const openWindow = (windowType) => {
        // 프로젝트 상세 윈도우는 중복 체크하지 않고 항상 업데이트
        if (windowType === 'project-detail') {
            // 이미 열려 있으면 제거하고 다시 추가
            const filteredWindows = openWindows.filter(w => w.id !== 'project-detail');
            const newWindow = {
                id: 'project-detail',
                title: selectedProject ? `${selectedProject.name} 상세` : '프로젝트 상세',
                minimized: false
            };
            setOpenWindows([...filteredWindows, newWindow]);

            // 위치 설정
            if (!windowPositions['project-detail']) {
                setWindowPositions(prev => ({
                    ...prev,
                    ['project-detail']: {
                        x: 150 + Math.random() * 100,
                        y: 150 + Math.random() * 50
                    }
                }));
            }
        } else if (!openWindows.some(w => w.id === windowType)) {
            const newWindow = {
                id: windowType,
                title: characterDonuts.find(item => item.id === windowType)?.name || windowType,
                minimized: false
            };
            setOpenWindows([...openWindows, newWindow]);

            if (!windowPositions[windowType]) {
                setWindowPositions(prev => ({
                    ...prev,
                    [windowType]: {
                        x: 100 + Math.random() * 200,
                        y: 100 + Math.random() * 100
                    }
                }));
            }
        }
    };

    // 윈도우 닫기
    const closeWindow = (windowId) => {
        setOpenWindows(openWindows.filter(w => w.id !== windowId));
    };

    // 윈도우 최소화
    const minimizeWindow = (windowId) => {
        setOpenWindows(openWindows.map(w =>
            w.id === windowId ? {...w, minimized: !w.minimized} : w
        ));
    };

    // GitHub 통계 가져오기
    const fetchGithubStats = async () => {
        try {
            // 사용자 정보 가져오기
            const userResponse = await fetch('https://api.github.com/users/moonyaejin');
            const userData = await userResponse.json();

            // 저장소 정보 가져오기
            const reposResponse = await fetch('https://api.github.com/users/moonyaejin/repos?per_page=100&sort=stars');
            const reposData = await reposResponse.json();

            // 총 Stars 계산
            const totalStars = reposData.reduce((acc, repo) => acc + repo.stargazers_count, 0);

            // 가장 인기있는 저장소
            const topRepo = reposData[0] ? {
                name: reposData[0].name,
                description: reposData[0].description,
                stars: reposData[0].stargazers_count,
                language: reposData[0].language,
                url: reposData[0].html_url
            } : null;

            // 언어별 통계 계산
            const languageCount = {};
            for (const repo of reposData) {
                if (repo.language) {
                    languageCount[repo.language] = (languageCount[repo.language] || 0) + 1;
                }
            }

            // 상위 3개 언어
            const topLanguages = Object.entries(languageCount)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([lang, count]) => ({
                    name: lang,
                    count: count,
                    percentage: Math.round((count / reposData.length) * 100)
                }));

            // 최근 이벤트 가져오기 (커밋, PR, 이슈 등)
            let recentCommits = [];
            let totalCommits = 0;
            let codingStreak = 0;

            try {
                const eventsResponse = await fetch('https://api.github.com/users/moonyaejin/events/public?per_page=100');
                const eventsData = await eventsResponse.json();

                // 최근 푸시 이벤트에서 커밋 정보 추출
                const pushEvents = eventsData.filter(event => event.type === 'PushEvent');
                recentCommits = pushEvents.slice(0, 5).map(event => ({
                    repo: event.repo.name.split('/')[1],
                    date: new Date(event.created_at).toLocaleDateString('ko-KR'),
                    commits: event.payload.commits?.length || 1,
                    message: event.payload.commits?.[0]?.message || 'No message'
                }));

                // 최근 30일 커밋 수 계산
                const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                const recentPushEvents = pushEvents.filter(event =>
                    new Date(event.created_at) > thirtyDaysAgo
                );
                totalCommits = recentPushEvents.reduce((sum, event) =>
                    sum + (event.payload.commits?.length || 1), 0
                );

                // 연속 코딩 일수 계산 (최근 활동 기준)
                const uniqueDays = new Set();
                eventsData.forEach(event => {
                    const date = new Date(event.created_at).toDateString();
                    uniqueDays.add(date);
                });

                const sortedDays = Array.from(uniqueDays)
                    .map(d => new Date(d))
                    .sort((a, b) => b - a);

                let streak = 0;
                const today = new Date();
                for (let i = 0; i < sortedDays.length - 1; i++) {
                    const diff = (sortedDays[i] - sortedDays[i + 1]) / (1000 * 60 * 60 * 24);
                    if (diff === 1) {
                        streak++;
                    } else {
                        break;
                    }
                }
                codingStreak = streak + 1;

            } catch (error) {
                console.error('Error fetching events:', error);
            }

            // 최근 활동 계산
            const sortedByPush = [...reposData].sort((a, b) =>
                new Date(b.pushed_at) - new Date(a.pushed_at)
            );
            const lastPushDate = sortedByPush[0]?.pushed_at;
            const daysSinceLastPush = lastPushDate ?
                Math.floor((Date.now() - new Date(lastPushDate)) / (1000 * 60 * 60 * 24)) : null;

            let lastActivity = '';
            if (daysSinceLastPush === 0) lastActivity = 'Today';
            else if (daysSinceLastPush === 1) lastActivity = 'Yesterday';
            else if (daysSinceLastPush < 7) lastActivity = `${daysSinceLastPush} days ago`;
            else if (daysSinceLastPush < 30) lastActivity = `${Math.floor(daysSinceLastPush / 7)} weeks ago`;
            else lastActivity = `${Math.floor(daysSinceLastPush / 30)} months ago`;

            // GitHub 가입 기간 계산
            const joinDate = new Date(userData.created_at);
            const now = new Date();
            const monthsSinceJoin = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24 * 30));
            const years = Math.floor(monthsSinceJoin / 12);
            const months = monthsSinceJoin % 12;
            const memberSince = years > 0 ?
                `${years}년 ${months}개월` :
                `${months}개월`;

            setGithubStats({
                repos: userData.public_repos || 0,
                followers: userData.followers || 0,
                following: userData.following || 0,
                stars: totalStars,
                topLanguages: topLanguages,
                topRepo: topRepo,
                lastActivity: lastActivity,
                joinDate: memberSince,
                totalContributions: reposData.length * 50, // 추정값
                totalCommits: totalCommits,
                recentCommits: recentCommits,
                codingStreak: codingStreak
            });
        } catch (error) {
            console.error('Error fetching GitHub stats:', error);
        }
    };

    // 윈도우 내용 렌더링
    const renderWindowContent = (windowType) => {
        switch (windowType) {
            case 'about me':
                return (
                    <div className="p-6 space-y-4 bg-gradient-to-br from-pink-50 to-purple-50">
                        <div className="flex items-start space-x-4">
                            <div className="text-6xl animate-pulse">🚀</div>
                            <div className="flex-1">
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                    문예진
                                </h2>
                                <p className="text-lg text-gray-700 mt-1">
                                    동시성 문제를 예측하고, 견고한 아키텍처로 해결하는 백엔드 개발자
                                </p>
                            </div>
                        </div>

                        {/* 개발 철학 */}
                        <div
                            className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                            <h3 className="font-bold text-purple-900 mb-2">🎓 EDUCATION</h3>
                            <p className="text-sm text-gray-700 italic">
                                <b>경기대학교 (2021.03 - 2025.02) <br/></b>
                                경영정보학과 전공 <br/>
                                컴퓨터공학과 복수전공 <br/> <br/>
                                항해 플러스 LITE 2기 (2025.8 - 2025.11)
                            </p>
                        </div>

                        {/* 기술 스택 */}
                        <div className="space-y-3">
                            <h3 className="font-bold text-gray-800">🛠️ 기술 스택</h3>
                            <div className="flex flex-wrap gap-2">
                                {/* 백엔드 핵심 */}
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-full text-sm font-medium">
                        Spring Boot
                    </span>
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 rounded-full text-sm font-medium">
                        Java
                    </span>
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 rounded-full text-sm font-medium">
                        JPA/Hibernate
                    </span>

                                {/* 데이터베이스 & 캐싱 */}
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 rounded-full text-sm font-medium">
                        MySQL
                    </span>
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-red-100 to-pink-100 text-red-800 rounded-full text-sm font-medium">
                        Redis
                    </span>

                                {/* 아키텍처 & 설계 */}
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 rounded-full text-sm font-medium">
                        Hexagonal Architecture
                    </span>
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-teal-100 to-green-100 text-teal-800 rounded-full text-sm font-medium">
                        DDD
                    </span>
                                <span
                                    className="px-3 py-1.5 bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 rounded-full text-sm font-medium">
                        TDD
                    </span>
                            </div>
                        </div>

                        {/* 개발 철학 */}
                        <div
                            className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                            <p className="text-sm text-gray-700 italic">
                                "발생할 수 있는 문제를 미리 예측하고 방어적으로 코딩합니다.
                                테스트 가능한 코드, 명확한 도메인 모델, 그리고 확장 가능한 아키텍처를 통해
                                비즈니스 가치를 안정적으로 전달하는 것이 백엔드 개발자의 책임이라고 믿습니다."
                            </p>
                        </div>

                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-600">
                    함께 성장하고 싶은 개발자 문예진입니다 ✨
                </span>
                        </div>
                    </div>
                );
            case 'my projects':
                return (
                    <div className="p-6 bg-gradient-to-br from-yellow-50 to-orange-50">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            My Projects <span>⭐</span>
                        </h2>
                        <div className="grid grid-cols-2 gap-4">
                            {detailedProjects.map((project, i) => (
                                <div
                                    key={i}
                                    className={`bg-gradient-to-br ${project.color} rounded-2xl p-4 hover:shadow-lg transition-all cursor-pointer hover:scale-105`}
                                    onClick={() => {
                                        setSelectedProject(project);
                                        setProjectTab('overview');
                                        // setTimeout을 사용해 state 업데이트 후 윈도우 열기
                                        setTimeout(() => {
                                            openWindow('project-detail');
                                        }, 0);
                                    }}
                                >
                                    <div className="text-4xl mb-2 text-center">{project.emoji}</div>
                                    <h3 className="font-semibold text-gray-800 text-center">{project.name}</h3>
                                    <p className="text-sm text-gray-600 text-center mb-1">{project.period}</p>
                                    <p className="text-xs text-gray-500 text-center">{project.teamSize}</p>
                                    <div className="mt-2 text-center">
                                        <span className="text-xs text-indigo-600 font-medium">
                                            클릭하여 상세보기 →
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 text-center">
                            <a
                                href="https://github.com/moonyaejin"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-gray-600 hover:text-gray-800 underline"
                            >
                                View more on GitHub →
                            </a>
                        </div>
                    </div>
                );
            case 'github':
                return (
                    <div className="p-6 bg-gradient-to-br from-blue-50 to-purple-50">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            GitHub Profile <span>🐱</span>
                        </h2>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
                                <Github className="w-5 h-5 text-gray-800"/>
                                <a href="https://github.com/moonyaejin" target="_blank" rel="noopener noreferrer"
                                   className="text-blue-600 hover:underline">
                                    moonyaejin
                                </a>
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                    {githubStats.lastActivity ? `🟢 ${githubStats.lastActivity}` : '🟢 Active'}
                                </span>
                            </div>

                            {/* 주요 통계 */}
                            <div className="grid grid-cols-2 gap-3">
                                <div
                                    className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl p-3 text-center">
                                    <div className="text-2xl mb-1">📚</div>
                                    <div className="text-sm font-semibold">{githubStats.repos} Repos</div>
                                </div>
                                <div
                                    className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl p-3 text-center">
                                    <div className="text-2xl mb-1">🗓️</div>
                                    <div className="text-sm font-semibold">가입한지 {githubStats.joinDate}</div>
                                </div>
                            </div>

                            {/* 가장 많이 사용한 언어 */}
                            {githubStats.topLanguages.length > 0 && (
                                <div className="bg-white/60 rounded-xl p-3">
                                    <p className="text-xs font-semibold text-gray-600 mb-2">💻 Most Used Languages</p>
                                    <div className="space-y-2">
                                        {githubStats.topLanguages.map((lang, i) => (
                                            <div key={i} className="flex items-center justify-between">
                                                <span className="text-sm">{lang.name}</span>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-20 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-gradient-to-r from-blue-400 to-purple-400 h-2 rounded-full"
                                                            style={{width: `${lang.percentage}%`}}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-gray-500">{lang.percentage}%</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 최근 커밋 활동 */}
                            {githubStats.recentCommits && githubStats.recentCommits.length > 0 && (
                                <div className="bg-white/60 rounded-xl p-3">
                                    <p className="text-xs font-semibold text-gray-600 mb-2">📝 Recent Commits</p>
                                    <div className="space-y-1">
                                        {githubStats.recentCommits.slice(0, 3).map((commit, i) => (
                                            <div key={i} className="flex justify-between items-center text-xs">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-gray-500">•</span>
                                                    <span className="font-medium text-gray-700">{commit.repo}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-green-100 text-green-700 px-1 py-0.5 rounded">
                                                        {commit.commits} commit{commit.commits > 1 ? 's' : ''}
                                                    </span>
                                                    <span className="text-gray-500">{commit.date}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'contact':
                return (
                    <div className="p-6 bg-gradient-to-br from-pink-50 to-red-50">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            Contact <span>📞️</span>
                        </h2>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
                                <Mail className="w-5 h-5 text-pink-500"/>
                                <span>i0209i80@gmail.com</span>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
                                <Mail className="w-5 h-5 text-pink-500"/>
                                <span>moon_xx_@naver.com</span>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
                                <FaDiscord className="w-5 h-5 text-pink-500"/>
                                <span>xaexix</span>
                            </div>
                        </div>
                    </div>
                );
            case 'blog':
                return (
                    <div className="p-6 bg-gradient-to-br from-pink-50 to-purple-50">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            My Velog Series <span>🎀</span>
                        </h2>
                        <div className="space-y-3">
                            {velogSeries.map((series) => (
                                <article
                                    key={series.id}
                                    className="bg-white/70 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer"
                                    onClick={() => window.open(series.link, '_blank')}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="text-3xl">{series.thumbnail}</div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-lg">{series.title}</h3>
                                            <p className="text-gray-600 text-sm mt-1">
                                                {series.description}
                                            </p>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-pink-200">

                            <a href="https://velog.io/@xaexix/series"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-400 to-purple-400 text-white rounded-full hover:from-pink-500 hover:to-purple-500 transition-all"
                            >
                            <span>Velog 보기</span>
                            <span>→</span>
                        </a>
                    </div>
            </div>
            );
            case 'project-detail':
                if (!selectedProject) {
                    return (
                        <div className="p-6 text-center text-gray-500">
                            프로젝트를 선택해주세요.
                        </div>
                    );
                }
                return (
                    <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                <span>{selectedProject.emoji}</span>
                                {selectedProject.name}
                            </h2>
                            <a
                                href={`https://github.com/moonyaejin/${selectedProject.repo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm bg-gray-800 text-white px-3 py-1 rounded-full hover:bg-gray-700"
                            >
                                GitHub →
                            </a>
                        </div>

                        {/* 탭 메뉴 */}
                        <div className="flex gap-2 mb-4 border-b">
                            {['overview', 'tech', 'features', 'performance'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setProjectTab(tab)}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                                        projectTab === tab
                                            ? 'text-indigo-600 border-b-2 border-indigo-600'
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    {tab === 'overview' && '개요'}
                                    {tab === 'tech' && '기술 스택'}
                                    {tab === 'features' && '주요 기능'}
                                    {tab === 'performance' && '성과'}
                                </button>
                            ))}
                        </div>

                        {/* 탭 컨텐츠 */}
                        {projectTab === 'overview' && (
                            <div className="space-y-4">
                                <div className="bg-white/70 rounded-lg p-4">
                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <p className="text-xs text-gray-500">기간</p>
                                            <p className="font-medium">{selectedProject.period}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">팀 규모</p>
                                            <p className="font-medium">{selectedProject.teamSize}</p>
                                        </div>
                                    </div>

                                    <h4 className="font-semibold text-gray-800 mb-2">🎯 주요 성과</h4>
                                    <ul className="space-y-1">
                                        {selectedProject.achievements.map((achievement, i) => (
                                            <li key={i} className="text-sm text-gray-700 flex items-start">
                                                <span className="text-indigo-500 mr-2">•</span>
                                                <span>{achievement}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {selectedProject.challenges && (
                                    <div className="bg-yellow-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-800 mb-2">💡 도전 과제와 해결</h4>
                                        <p className="text-sm text-gray-700">{selectedProject.challenges}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {projectTab === 'tech' && (
                            <div className="space-y-4">
                                {Object.entries(selectedProject.techDetail).map(([category, techs]) => (
                                    <div key={category} className="bg-white/70 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-800 mb-2 capitalize">
                                            {category === 'backend' && '🔧 백엔드'}
                                            {category === 'database' && '💾 데이터베이스'}
                                            {category === 'infra' && '☁️ 인프라'}
                                            {category === 'testing' && '🧪 테스팅'}
                                            {category === 'frontend' && '🎨 프론트엔드'}
                                            {category === 'api' && '🔌 API'}
                                            {category === 'realtime' && '⚡ 실시간'}
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {techs.map((tech, i) => (
                                                <span
                                                    key={i}
                                                    className="px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full text-xs font-medium"
                                                >
                                                    {tech}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {projectTab === 'features' && selectedProject.keyFeatures && (
                            <div className="space-y-3">
                                {Object.entries(selectedProject.keyFeatures).map(([feature, description]) => (
                                    <div key={feature} className="bg-white/70 rounded-lg p-4">
                                        <h4 className="font-semibold text-indigo-700 mb-1">{feature}</h4>
                                        <p className="text-sm text-gray-600">{description}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {projectTab === 'performance' && selectedProject.performanceMetrics && (
                            <div className="grid grid-cols-2 gap-4">
                                {Object.entries(selectedProject.performanceMetrics).map(([metric, value]) => (
                                    <div key={metric} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-4 text-center">
                                        <p className="text-xs text-gray-600 mb-1">{metric}</p>
                                        <p className="text-lg font-bold text-indigo-700">{value}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            case 'resume':
                return (
                    <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            Resume <span>📋</span>
                        </h2>
                        <div className="space-y-4">
                            <div className="bg-white/70 rounded-xl p-6">
                                <h3 className="font-semibold text-lg mb-3">상세 이력서</h3>
                                <p className="text-gray-600 mb-4">
                                    개별 프로젝트의 상세 설명, 기술 스택,
                                    문제 해결 과정 등이 정리되어 있습니다.
                                </p>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <span>📌</span> 프로젝트별 상세 설명
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <span>💡</span> 문제 해결 과정과 성장 경험
                                    </div>
                                </div>
                            </div>

                            <a href="https://www.notion.so/xaexix/1c743eff7a4f807dbf34e5507138df79"
                               target="_blank"
                               rel="noopener noreferrer"
                               className="block bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl p-4 text-center hover:from-indigo-600 hover:to-purple-600 transition-all transform hover:scale-105"
                            >
                                <div className="text-lg font-semibold mb-1">
                                    노션에서 상세 이력서 보기
                                </div>
                                <div className="text-sm opacity-90">
                                    Click to view detailed portfolio →
                                </div>
                            </a>

                            <div className="bg-purple-50 rounded-lg p-3">
                                <p className="text-xs text-purple-600 text-center">
                                    💼 더 자세한 프로젝트 설명과 기술 경험을 확인하실 수 있습니다
                                </p>
                            </div>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="p-6 bg-gradient-to-br from-orange-50 to-yellow-50">
                        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            {characterDonuts.find(item => item.id === windowType)?.name || windowType} <span>🚧</span>
                        </h2>
                        <p className="text-gray-600">This sweet donut is still in the oven... 🍩</p>
                        <p className="text-gray-500 text-sm mt-2">Adding more sugar and love!</p>
                    </div>
                );
        }
    };

    return (
        <div className="w-full h-screen relative overflow-hidden bg-white">
            {isSmallScreen ? (
                <div className="flex flex-col h-full bg-gradient-to-br from-pink-100 to-purple-100">
                    {/* 모바일 헤더 */}
                    <div className="bg-pink-300/80 backdrop-blur-md p-4 text-white">
                        <h1 className="text-xl font-bold flex items-center gap-2">
                            🍩 MOONYAEJIN
                        </h1>
                        <p className="text-xs mt-1">Backend Developer Portfolio</p>
                    </div>

                    {/* 모바일 메뉴 - 도넛 배경 없이 깔끔한 그리드 */}
                    <div className="flex-1 grid grid-cols-2 gap-4 p-4">
                        {characterDonuts.map(character => (
                            <button
                                key={character.id}
                                onClick={() => openWindow(character.id)}
                                className="bg-white/80 rounded-xl p-4 flex flex-col items-center justify-center shadow-lg active:scale-95 transition-transform"
                            >
                                <IconDisplay item={character} size="large" />
                                <span className="text-sm font-medium text-gray-700 mt-2">{character.name}</span>
                            </button>
                        ))}
                    </div>

                    {/* 모바일 열린 윈도우 */}
                    {openWindows.length > 0 && (
                        <div className="fixed inset-0 bg-white z-50">
                            <div className="bg-gradient-to-r from-pink-300 to-purple-300 p-4 flex justify-between items-center">
                            <span className="text-white font-bold">
                                {openWindows[openWindows.length - 1].id === 'project-detail' && selectedProject
                                    ? `${selectedProject.emoji} ${selectedProject.name}`
                                    : openWindows[openWindows.length - 1].title}
                            </span>
                                <button
                                    onClick={() => {
                                        const lastWindow = openWindows[openWindows.length - 1];
                                        closeWindow(lastWindow.id);
                                        if (lastWindow.id === 'project-detail') {
                                            setSelectedProject(null);
                                            setProjectTab('overview');
                                        }
                                    }}
                                    className="text-white text-2xl"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="overflow-y-auto h-[calc(100vh-60px)]">
                                {renderWindowContent(openWindows[openWindows.length - 1].id)}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {/* 데스크톱/태블릿용 - 도넛 배경과 함께 */}
                    {/* 배경 도넛과 캐릭터를 함께 감싸는 컨테이너 */}
                    <div
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                        style={{
                            position: 'relative',
                            width: '140vw',
                            height: '140vh',
                            maxWidth: '1600px',
                            maxHeight: '1400px',
                            backgroundImage: `url('/donut-bg.jpg')`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}
                    >
                        {/* 캐릭터 도넛들 - 컨테이너 내부에서 상대적 위치 */}
                        {characterDonuts.map(character => (
                            <div
                                key={character.id}
                                className="absolute cursor-pointer hover:scale-110 transition-transform duration-300"
                                style={{
                                    left: character.x,
                                    top: character.y,
                                    transform: 'translate(-50%, -50%)'
                                }}
                                onClick={() => openWindow(character.id)}
                            >
                                <div className="flex flex-col items-center space-y-2">
                                    <div className="relative">
                                        <IconDisplay item={character} size="large" />
                                    </div>
                                    <span className="mt-1 bg-white/80 text-gray-800 text-xs px-2 py-1 rounded shadow-md whitespace-nowrap">
                                        {character.name}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 메뉴바 */}
                    <div
                        className="absolute top-0 left-0 right-0 h-8 bg-pink-300/80 backdrop-blur-md border-b border-pink-400/50 flex items-center justify-between px-4 text-white text-sm z-50">
                        <div className="flex items-center space-x-4">
                        <span className="font-bold flex items-center gap-1">
                            🍩 MOONYAEJIN
                        </span>
                            <span>File</span>
                            <span>Edit</span>
                        </div>
                        <div className="flex items-center space-x-4">
                            <span>{time.toLocaleTimeString()}</span>
                        </div>
                    </div>

                    {/* 윈도우들 */}
                    {openWindows.map(window => {
                        const position = windowPositions[window.id] || {x: 100, y: 100};
                        return (
                            <div
                                key={window.id}
                                className={`absolute bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border-2 border-pink-300/50 overflow-hidden transition-all ${
                                    window.minimized ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
                                }`}
                                style={{
                                    left: position.x,
                                    top: position.y,
                                    width: window.id === 'project-detail' ? '600px' : '450px',
                                    minHeight: '350px',
                                    zIndex: draggedWindow === window.id ? 1000 : 100
                                }}
                            >
                                {/* 윈도우 헤더 */}
                                <div
                                    className="h-12 bg-gradient-to-r from-pink-300 to-purple-300 border-b border-pink-400/50 flex items-center justify-between px-4 cursor-move relative"
                                    onMouseDown={(e) => handleMouseDown(e, window.id)}
                                >
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => closeWindow(window.id)}
                                            className="w-4 h-4 bg-red-400 rounded-full hover:bg-red-500 transition-colors shadow-sm"
                                        ></button>
                                        <button
                                            onClick={() => minimizeWindow(window.id)}
                                            className="w-4 h-4 bg-yellow-400 rounded-full hover:bg-yellow-500 transition-colors shadow-sm"
                                        ></button>
                                        <button
                                            className="w-4 h-4 bg-green-400 rounded-full hover:bg-green-500 transition-colors shadow-sm"></button>
                                    </div>
                                    <span className="text-sm font-bold text-white flex items-center gap-1">
                                    {window.title} <span>🍩</span>
                                </span>
                                    <div className="w-16"></div>
                                </div>

                                {/* 윈도우 내용 */}
                                {renderWindowContent(window.id)}
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};

export default CharacterDonutPortfolio;